import React, { useContext, useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { AccountContext, AccountContextValue } from '@/contexts/account'
import { isScreenReadOnlyForUi } from '@/libs/hotel-pms-permission'
import { LoadingFull } from '@/components/molecules/loading-full'
import { hasReservationTaskFilter, toTaskStaffOptions } from '@/utils/reservation-task'
import { useReservationTaskList } from './use-reservation-task-list'
import { HandlingStatusPopoverStyle } from './handling-status-menu'
import { ReservationTaskToolbar } from './reservation-task-toolbar'
import { ReservationTaskFilterPanel } from './reservation-task-filter-panel'
import { ReservationTaskTable } from './reservation-task-table'

type Props = {
  /** 未対応件数を取り直す。件数は宿泊管理画面（list.tsx）が持ち、タブ見出しのバッジに出す */
  onTasksChanged: () => void
}

export const ReservationTaskTab: React.FC<Props> = ({ onTasksChanged }) => {
  const { permissions, permissionsLoaded, permissionsError, employeeStaff } = useContext<AccountContextValue>(AccountContext)
  // 読み取り専用の判定はここで1回だけ行い、下の部品に渡す。書き込みは aipass.ts の共通の権限確認でも止まる
  const isReadOnly = isScreenReadOnlyForUi(permissions, 'reservation', permissionsLoaded, permissionsError)
  const staffOptions = useMemo(() => toTaskStaffOptions(employeeStaff), [employeeStaff])
  const list = useReservationTaskList({ onTasksChanged })
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false)

  // 行を選ぶと帯からフィルターボタンが消え、パネルを閉じられなくなるため、選び始めたらパネルを閉じる
  const toggleTask = (taskId: number, checked: boolean) => {
    setIsFilterPanelOpen(false)
    list.toggleTask(taskId, checked)
  }
  const toggleAllTasks = (checked: boolean) => {
    setIsFilterPanelOpen(false)
    list.toggleAllTasks(checked)
  }

  return (
    <div css={containerStyle}>
      <HandlingStatusPopoverStyle />
      <ReservationTaskToolbar
        selectedCount={list.selectedTaskIds.length}
        total={list.total}
        page={list.query.page}
        isLoading={list.isLoading}
        isReadOnly={isReadOnly}
        isFiltering={hasReservationTaskFilter(list.query.filter)}
        onToggleFilterPanel={() => setIsFilterPanelOpen(isOpen => !isOpen)}
        onReload={list.reload}
        onChangePage={list.changePage}
        onBulkChangeHandlingStatus={handlingStatus => list.updateTasks(list.selectedTaskIds, { handlingStatus })}
      />
      {isFilterPanelOpen && (
        <ReservationTaskFilterPanel
          filter={list.query.filter}
          staffOptions={staffOptions}
          onApply={filter => {
            setIsFilterPanelOpen(false)
            list.applyFilter(filter)
          }}
        />
      )}
      <ReservationTaskTable
        tasks={list.tasks}
        sort={list.query.sort}
        selectedTaskIds={list.selectedTaskIds}
        staffOptions={staffOptions}
        isReadOnly={isReadOnly}
        isLoading={list.isLoading}
        scrollRef={list.scrollRef}
        onChangeSort={list.changeSort}
        onToggleTask={toggleTask}
        onToggleAllTasks={toggleAllTasks}
        onUpdate={list.updateTasks}
      />
      <LoadingFull isLoading={list.isLoading} />
    </div>
  )
}

const containerStyle = css({
  position: 'relative',
})
